import useAlias from '@alias';
import { Grid2 as Grid } from '@mui/material';
import { useConfigContext } from '@src/pages/Home/pages/Config/ConfigWrapperContext';
import storeConnector from '@src/store/storeConnector';
import { SiteMeta } from '@src/types/SiteMeta';
import { ControlModes } from '@src/types/Stacks';
import { useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { getQuickConfigFields } from '../../utils';
import { useTemplateForm } from './TemplatesCard/hooks/useTemplateForm';
import { useTemplateStatus } from './TemplatesCard/hooks/useTemplateStatus';
import { useTemplateToggle } from './TemplatesCard/hooks/useTemplateToggle';
import { TemplateCardConfig } from './TemplatesCard/TemplateCardConfig';
import { TemplateCardHeader } from './TemplatesCard/TemplateCardHeader';
import TemplatesEnableConfirm from './TemplatesEnableConfirm';
import TemplatesFullConfig from './TemplatesFullConfig';

interface TemplatesCardProps {
  controlMode: ControlModes;
  siteMeta: SiteMeta;
  handleUpdateStack: (controlMode: ControlModes) => void;
  isDefault?: boolean;
}

function TemplatesCard({
  controlMode,
  siteMeta,
  handleUpdateStack,
  isDefault,
}: TemplatesCardProps) {
  const alias = useAlias();
  const { controlAuthority } = useConfigContext();
  const [open, setOpen] = useState(false);

  const { scadaStatus, hmiStatus, activeStatus, statusTypes } =
    useTemplateStatus(controlMode, siteMeta, controlAuthority, alias);

  const { methods, onSubmit } = useTemplateForm(
    controlMode,
    handleUpdateStack,
    () => setOpen(false),
  );

  const { confirmOpen, setConfirmOpen, handleToggleUpdate } =
    useTemplateToggle();

  const quickConfigFields = useMemo(
    () => getQuickConfigFields(controlMode),
    [controlMode],
  );

  const handleOpen = () => {
    setOpen(true);
  };

  return (
    <FormProvider {...methods}>
      <Grid container spacing={2}>
        <TemplateCardHeader
          controlMode={controlMode}
          isDefault={isDefault}
          scadaStatus={scadaStatus}
          activeStatus={activeStatus}
          hmiStatus={hmiStatus}
          statusTypes={statusTypes}
        />
        <TemplateCardConfig
          quickConfigFields={quickConfigFields}
          activeStatus={activeStatus}
          controlAuthority={controlAuthority}
          onSubmit={methods.handleSubmit(onSubmit)}
          handleToggleUpdate={handleToggleUpdate}
          handleOpen={handleOpen}
        />
        {open && (
          <TemplatesFullConfig
            setOpen={setOpen}
            controlMode={controlMode}
            onSubmit={onSubmit}
          />
        )}
        {confirmOpen && (
          <TemplatesEnableConfirm
            setConfirmOpen={setConfirmOpen}
            modeName={controlMode.name}
            activeStatus={activeStatus}
          />
        )}
      </Grid>
    </FormProvider>
  );
}

export default storeConnector(TemplatesCard, {
  config: ['siteMeta'],
});
